import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useEffect } from 'react'
import * as prismic from '@prismicio/client'
import { SliceZone } from '@prismicio/react'
import { ArticleCategory } from 'enums'
import { createInstance } from 'i18next'

import { Community } from 'components/Community'
import { IMarkupSchemas } from 'components/SEOMarkupSchema'
import Title from 'components/Title'
import { components } from 'slices'

import { useLanguageSettings } from 'context/language-manager'

import { PageDocument } from 'types/prismic'
import { getEnv } from 'utils/env/env.util'
import { getFullPageByUID, getPrismicLangCode } from 'utils/prismic'
import { createClient } from 'env/prismicio'

import { CommunityPosts } from '../index'

const { baseUrl } = getEnv()
const CommunityPage = (props: {
  page: PageDocument<string>
  communityPosts: CommunityPosts
  locale: string
}) => {
  const router = useRouter()
  const { switchLanguage } = useLanguageSettings()

  useEffect(() => {
    switchLanguage(
      router.asPath.endsWith('darbo-rinkos-tendencijos') ? 'lt' : 'en'
    )
  }, [props.page, router.asPath, switchLanguage])

  return (
    <>
      <SliceZone slices={props.page.data.slices} components={components} />
      <Title className="px-5 pb-16 pt-20 sm:px-0 xl:px-0 ">
        {props.page.data.title[0]?.text ?? ''}
      </Title>
      <Community communityPosts={props.communityPosts} key={props.locale} />
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({
  locale: currentLocale,
  previewData,
  defaultLocale
}) => {
  const locale = currentLocale ?? defaultLocale ?? 'en'
  const client = createClient({ previewData })

  const category = ArticleCategory.LabourMarketInsights

  const pagePartsPromise = getFullPageByUID(
    'page',
    category,
    { locale },
    { previewData }
  )

  const communityPostsPromise = client.getByType('communitypost', {
    lang: getPrismicLangCode(locale),
    pageSize: 18,
    predicates: [
      prismic.predicate.fulltext('my.communitypost.category', category)
    ],
    orderings: [
      { field: 'my.communitypost.publishDate', direction: 'desc' },
      { field: 'document.first_publication_date', direction: 'desc' }
    ]
  })

  const i18nPromise = serverSideTranslations(locale, [
    'actions',
    'inputs',
    'community',
    'crm',
    'meta'
  ])

  const [pageParts, i18n, communityPosts] = await Promise.all([
    pagePartsPromise,
    i18nPromise,
    communityPostsPromise
  ])
  const { page, ...layoutParts } = pageParts

  const i18nInstance = createInstance({
    ...i18n._nextI18Next.userConfig,
    lng: i18n._nextI18Next.initialLocale,
    ns: i18n._nextI18Next.ns,
    resources: i18n._nextI18Next.initialI18nStore
  })
  i18nInstance.init()

  const markupSchema: IMarkupSchemas = [
    {
      type: 'BreadcrumbList',
      data: {
        items: [
          { name: 'Home', item: baseUrl },
          { name: 'Expert Insights', item: `${baseUrl}/expert-insights` },
          {
            name: i18nInstance.t(`category.${category}`, {
              ns: 'community',
              lng: 'en'
            })
          }
        ]
      }
    }
  ]
  return {
    props: {
      ...i18n,
      page,
      communityPosts,
      locale,
      seo: {
        title: `category.${category}.title`,
        metaDescription: `category.${category}.description`
      },
      internal: {
        layout: layoutParts
      },
      markupSchema
    },
    revalidate: 60 * 60 * 1 //4 hours
  }
}

export default CommunityPage
